import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { RootState } from "../store"
import { STATUS } from "../../utils/const"
import BaseApi from "../../api/api"
import { NotificationService } from "../../features/NotificationService/NotificationService"
import { BaseWBRecodeItemType } from "../../components/ShopComponent/types"
import { setFeedShopCalcIdent, updateFeedShopsState } from "./feedShopsSlice"

export type ShopInfo = {
  lastPricesUpdated?: string
  lastStocksUpdated?: string
  errors?: { type: string; errors: string[] }[]
  status?: STATUS
  isExistRecode?: boolean
  recodeFields: string[]
  calcIdent: string
  updateType: string
}

type initialShopsStateType = {
  [key: string]: ShopInfo
}

const initialState: initialShopsStateType = {}

const shopsSlice = createSlice({
  name: "shopsSlice",
  initialState,
  reducers: {
    setShopsState: (state, action: PayloadAction<initialShopsStateType>) => {
      Object.keys(action.payload).forEach((key) => {
        state[key] = action.payload[key]
      })
    },
    setShopCalcIdent: (
      state,
      action: PayloadAction<{ shop: string; calcIdent: string }>,
    ) => {
      const { shop, calcIdent } = action.payload
      if (state[shop]) {
        state[shop].calcIdent = calcIdent
      } else {
        state[shop] = { calcIdent, recodeFields: [], updateType: "" }
      }
    },
  },
})

export const selectShopsStore = (state: RootState) => state.shopsState

export default shopsSlice.reducer

export const { setShopsState, setShopCalcIdent } = shopsSlice.actions

export const updateShopsState = createAsyncThunk(
  "updateShopsState",
  (_, { dispatch }) => {
    return BaseApi.shopsState()
      .then((response) => {
        if (response.data) {
          dispatch(setShopsState(response.data))
        }
      })
      .catch((error) => {
        console.error(error)
      })
  },
)

export const updateShopCalcIdent = createAsyncThunk(
  "shops/calcIdent/update",
  async (
    {
      shop,
      calcIdent,
      isFeedShop,
    }: { shop: string; calcIdent: string; isFeedShop?: boolean },
    { dispatch },
  ) => {
    // оптимистичное обновление: обычные магазины → shopsState, feed → feedShopsState
    dispatch(
      isFeedShop
        ? setFeedShopCalcIdent({ shop, calcIdent })
        : setShopCalcIdent({ shop, calcIdent }),
    )
    return BaseApi.updateShopCalcIdent(shop, calcIdent)
      .then((response) => {
        if (response.data) {
          NotificationService.showNotification({
            message: response.data.message,
            level: (response.data.success && "success") || "error",
          })
        }
        return response.data
      })
      .catch((error) => {
        console.error("updateShopCalcIdent error:", error)
        NotificationService.showNotification({
          message:
            error?.response?.data?.message || "Ошибка при смене калькулятора",
          level: "error",
        })
        // откатываем оптимистичное локальное изменение
        dispatch(isFeedShop ? updateFeedShopsState() : updateShopsState())
        throw error
      })
  },
)

export const uploadWbRecode = <T extends BaseWBRecodeItemType>(
  shopIdent: string,
  rows: T[],
) => {
  BaseApi.uploadWbRecode(shopIdent, rows).then((response) => {
    NotificationService.showNotification({
      message: response.data.message,
      level: (response.data.success && "success") || "error",
    })
  })
}

export const deleteWbRecode = (shopIdent: string) => {
  BaseApi.deleteWbRecode(shopIdent)
    .then((response) => {
      NotificationService.showNotification({
        message: response.data.message,
        level: (response.data.success && "success") || "error",
      })
    })
    .catch((error) => {
      console.error("deleteWbRecode error:", error)
      NotificationService.showNotification({
        message:
          error?.response?.data?.message || "Ошибка при удалении перекодировки",
        level: "error",
      })
    })
}

export const getWbRecode = <T extends BaseWBRecodeItemType>(
  shopIdent: string,
): Promise<T[]> => {
  return BaseApi.getWbRecode(shopIdent)
}

export const updateWbStocks = (args: {
  shopIdent: string
  selectedStorageId: string
  updatePrices: boolean
  updateStocks: boolean
  isReturnToPromo?: boolean
}) => {
  const {
    shopIdent,
    selectedStorageId,
    updatePrices,
    updateStocks,
    isReturnToPromo,
  } = args
  BaseApi.updateWbStocks(
    shopIdent,
    selectedStorageId,
    updatePrices,
    updateStocks,
    isReturnToPromo,
  )
    .then((response) => {
      if (response.data) {
        NotificationService.showNotification({
          message: response.data.message,
          level: (response.data.success && "success") || "error",
        })
      }
    })
    .catch((error) => {
      console.error("updateWbStocks error:", error)
      NotificationService.showNotification({
        message:
          error?.response?.data?.message || "Ошибка при обновлении остатков",
        level: "error",
      })
    })
}
